import { useCallback, useRef, useState } from "react";

/**
 * Reports whether the element carrying `ref` scrolls (content overflows its
 * box). `ref` is a callback ref so consumers that swap the observed element
 * between renders (loading → content branches) re-observe automatically.
 */
export function useOverflowDetection<T extends HTMLElement = HTMLDivElement>() {
  const [hasOverflow, setHasOverflow] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((element: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!element) {
      setHasOverflow(false);
      return;
    }

    const check = () =>
      setHasOverflow(element.scrollHeight > element.clientHeight);

    check();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(check);
    observer.observe(element);
    Array.from(element.children).forEach((child) => observer.observe(child));
    observerRef.current = observer;
  }, []);

  return { ref, hasOverflow };
}
